import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { UserLeaguesProvider } from './contexts/UserLeaguesContext';
import { HomeSummaryProvider } from './contexts/HomeSummaryContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { HomePage } from './pages/HomePage';
import { OAuthCallback } from './pages/OAuthCallback';
import { NotFound } from './pages/NotFound';
import { AuthPage } from './pages/AuthPage';
import './index.css';

// Lazy so the landing, sign-in, and legal routes do not preload the dashboard
// and its Recharts vendor chunk (Track H3).
const LeagueLayout = lazy(() => import('./layouts/LeagueLayout').then(m => ({ default: m.LeagueLayout })));
const ExampleDashboard = lazy(() => import('./pages/ExampleDashboard').then(m => ({ default: m.ExampleDashboard })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const SharedLeaguePage = lazy(() => import('./pages/SharedLeaguePage').then(m => ({ default: m.SharedLeaguePage })));
const JoinSharedLeaguePage = lazy(() => import('./pages/JoinSharedLeaguePage').then(m => ({ default: m.JoinSharedLeaguePage })));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({ default: m.TermsOfService })));

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) {
  throw new Error('VITE_CLERK_PUBLISHABLE_KEY is not set. See frontend/.env.example');
}

const authCopy = {
  signIn: { start: {
    title: 'Sign in',
    subtitle: 'Welcome back. Pick up where your league left off.',
    actionText: 'No account?',
    actionLink: 'Create one',
  } },
  signUp: { start: {
    title: 'Create your account',
    subtitle: 'Connect a Yahoo or ESPN league once you are in.',
    actionText: 'Already have an account?',
    actionLink: 'Sign in',
  } },
};

// Every Clerk surface (sign-in, sign-up, the account modal) reads
// the Ledger tokens, so it follows the light/dark theme.
const clerkAppearance = {
  variables: {
    fontFamily: 'inherit',
    borderRadius: '6px',
    colorBackground: 'var(--ledger-panel)',
    colorForeground: 'var(--ledger-ink)',
    colorMutedForeground: 'var(--ledger-muted)',
    colorPrimary: 'var(--ledger-ink)',
    colorPrimaryForeground: 'var(--ledger-panel)',
    colorInput: 'var(--ledger-panel)',
    colorInputForeground: 'var(--ledger-ink)',
    colorNeutral: 'var(--ledger-ink)',
    colorMuted: 'var(--ledger-rail)',
    colorBorder: 'var(--ledger-line)',
    colorRing: 'var(--ledger-focus)',
    colorDanger: 'var(--ledger-bad)',
    colorSuccess: 'var(--ledger-good)',
    colorWarning: 'var(--ledger-warn)',
  },
};

function ProtectedRoute() {
  const { isSignedIn, isLoaded } = useAuth();
  if (!isLoaded) return null;
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;
  return <Outlet />;
}

const router = createBrowserRouter([
  { path: '/', element: <HomePage /> },
  { path: '/sign-in/*', element: <AuthPage mode="sign-in" /> },
  { path: '/sign-up/*', element: <AuthPage mode="sign-up" /> },
  { path: '/auth/callback', element: <OAuthCallback /> },
  { path: '/example', element: <Suspense fallback={null}><ExampleDashboard /></Suspense> },
  { path: '/privacy', element: <Suspense fallback={null}><PrivacyPolicy /></Suspense> },
  { path: '/terms', element: <Suspense fallback={null}><TermsOfService /></Suspense> },
  {
    path: '/s/:token',
    element: <Suspense fallback={null}><SharedLeaguePage /></Suspense>,
    children: [
      { index: true, element: null },
      { path: 'season', element: null },
      { path: 'rivalry/:a/:b', element: null },
      { path: 'manager/:key', element: null },
      { path: ':tab', element: null },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      { path: '/settings', element: <Suspense fallback={null}><Settings /></Suspense> },
      { path: '/join/:token', element: <Suspense fallback={null}><JoinSharedLeaguePage /></Suspense> },
      {
        path: '/league/:leagueId',
        element: <Suspense fallback={null}><LeagueLayout /></Suspense>,
        children: [
          { index: true, element: null },
          { path: 'season', element: null },
          { path: 'rivalry/:a/:b', element: null },
          { path: 'manager/:key', element: null },
          { path: ':tab', element: null },
        ],
      },
    ],
  },
  { path: '*', element: <NotFound /> },
]);

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ThemeProvider>
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        localization={authCopy}
        appearance={clerkAppearance}
        signInUrl="/sign-in"
        signUpUrl="/sign-up"
        afterSignOutUrl="/"
        routerPush={to => router.navigate(to)}
        routerReplace={to => router.navigate(to, { replace: true })}
      >
        <UserLeaguesProvider>
          <HomeSummaryProvider>
            <RouterProvider router={router} />
          </HomeSummaryProvider>
        </UserLeaguesProvider>
      </ClerkProvider>
    </ThemeProvider>
  </React.StrictMode>
);
